// Progressive streaming of a lod-meta.json splat: first paint with the coarsest LOD only, then straight to the
// final LOD range. The engine keeps drawing each node's coarsest level until that node's final file has arrived
// (it switches per node, nearest first), so no intermediate level is downloaded that the final picture does not
// use. The final picture is the same as stepping one level at a time: the engine picks every node's level from
// the final range, the camera and the budget, not from the path taken.
import { Asset, Entity, version } from 'playcanvas';

// Chunk files fetched at the same time (callers pass more on desktop). The engine's default (2) leaves the link
// idle: every chunk is a meta.json round trip, then 7 textures, then GPU work, before the next one starts.
const CONCURRENCY = 3;

/**
 * @param {import('playcanvas').AppBase} app
 * @param {object} opts
 * @param {string} opts.url - lod-meta.json URL
 * @param {object} opts.lod - manifest.lod
 * @param {object} opts.profile - manifest.lod.profiles[x]
 * @param {number} [opts.concurrency] - chunk files loading at the same time
 * @param {(e:{level:number, finest:number, levels:number})=>void} [opts.onLevel] - progress: `level` falls from
 *   the coarsest to `finest` as the final files arrive (by the share still loading); `finest` = all there
 * @param {()=>void} [opts.onFirstFrame] - coarsest level is on screen
 * @returns {Promise<{entity: Entity, levels: number, asset: Asset}>}
 */
export function loadProgressive(app, opts) {
    return new Promise((resolve, reject) => {
        const asset = new Asset('tour', 'gsplat', { url: opts.url });
        asset.once('error', err => reject(new Error(`splat load failed: ${err}`)));
        asset.once('load', () => {
            const octree = asset.resource?.octree;
            const loader = octree?.assetLoader;
            if (loader && typeof loader.maxConcurrentLoads === 'number') loader.maxConcurrentLoads = opts.concurrency ?? CONCURRENCY;
            if (octree && typeof octree.submitRequests === 'function' && /^2\.23\./.test(version)) nearFirst(octree);

            const entity = new Entity('tour');
            entity.addComponent('gsplat', { asset });
            // PlayCanvas renders PLY-frame splats rotated 180 degrees about Z; the pipeline aligned
            // the scene so that, in this frame, Y is up and the floor is at 0 (same frame as the voxels)
            entity.setLocalEulerAngles(0, 0, 180);
            app.root.addChild(entity);

            const gs = entity.gsplat;
            gs.lodBaseDistance = opts.lod.baseDistance ?? 3;
            gs.lodMultiplier = opts.lod.multiplier ?? 2.5;
            setSplatBudget(app, asset, opts.profile.budget);

            const levels = gs.resource?.octree?.lodLevels ?? opts.lod.levels ?? 1;
            const worst = levels - 1;
            const finest = Math.min(Math.max(opts.profile.finest ?? 0, 0), worst);
            gs.lodRangeMin = worst;
            gs.lodRangeMax = worst;

            let first = true;
            // After a range change the engine requests the new chunks on a later frame, so "nothing
            // loading" right after the change does not mean the level has arrived
            let settle = 0;
            let most = 0, reported = worst;
            const report = (level) => {
                if (level >= reported && level !== finest) return;
                reported = level;
                opts.onLevel?.({ level, finest, levels });
            };
            const system = app.systems.gsplat;
            const onReady = (camera, layer, ready, loadingCount) => {
                if (loadingCount > 0) settle = Math.min(settle, 2);
                if (settle > 0) { settle--; return; }
                if (!first && loadingCount > 0) {
                    // Between the first paint and the final picture: progress by the share of files still loading
                    most = Math.max(most, loadingCount);
                    report(Math.max(finest + 1, finest + Math.ceil((worst - finest) * loadingCount / most)));
                }
                if (!ready || loadingCount !== 0) return;
                if (first) {
                    first = false;
                    opts.onFirstFrame?.();
                    if (worst > finest) {
                        opts.onLevel?.({ level: worst, finest, levels });
                        gs.lodRangeMin = finest;
                        // Let distance bands use any coarser level for far-away parts
                        gs.lodRangeMax = worst;
                        settle = 10;
                        return;
                    }
                }
                report(finest);
                system.off('frame:ready', onReady);
            };
            system.on('frame:ready', onReady);
            resolve({ entity, levels, asset });
        });
        app.assets.add(asset);
        app.assets.load(asset);
    });
}

/**
 * Splats drawn per frame for `asset`'s octree. The engine charges every chunk file in use a texture row of padding
 * against the budget (about sqrt(budget) splats). With one octree node per file that would push near nodes a level
 * coarser than the budget is meant to allow, so each node gets half a row on top.
 */
export function setSplatBudget(app, asset, budget) {
    const nodes = asset.resource?.octree?.nodes?.length ?? 0;
    app.scene.gsplat.splatBudget = budget + Math.round(nodes * Math.sqrt(budget) / 2);
}

/**
 * Fetch the nearest chunks first. PlayCanvas 2.23 ranks a request tier + (lodLevel + nearness) / lodLevels
 * (GSplatOctreeInstance.requestFile), so within a tier coarse files always win over near ones. Here every node
 * already shows its coarsest level, so nearness decides within a tier. Only the order changes, never which files
 * are loaded; other engine versions keep their own order.
 */
function nearFirst(octree) {
    const L = octree.lodLevels, submit = octree.submitRequests.bind(octree);
    octree.submitRequests = (requester, requests) => {
        for (const [fi, p] of requests) {
            const lod = octree.files[fi]?.lodLevel ?? 0;
            let tier = Math.floor(p), frac = p - tier;
            if (frac * L < lod - 1e-6) { tier--; frac++; }
            const near = Math.min(1, Math.max(0, frac * L - lod));
            requests.set(fi, tier + 0.001 + 0.99 * near + 0.001 * lod / L);
        }
        return submit(requester, requests);
    };
}
